import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, TrendingUp, CheckCircle2, Briefcase } from 'lucide-react';
import { applicationService } from '@core/supabase/services/applicationService';
import type { Application, ApplicationStatus } from '@core/types';

type Row = {
  memberId: string;
  memberName: string;
  total: number;
  reviewing: number;
  interviewing: number;
  offers: number;
  placed: number;
};

const REVIEWING_STATUSES: ApplicationStatus[] = ['submitted', 'viewed_by_client', 'shortlisted'];
const INTERVIEW_STATUSES: ApplicationStatus[] = [
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
];
const OFFER_STATUSES: ApplicationStatus[] = [
  'offer_extended',
  'offer_accepted',
  'offer_rejected',
];

const UNASSIGNED_ID = '__unassigned__';

const DEMO_MOCK_ENABLED = false;

const MOCK_TEAM_MEMBERS = [
  { id: 'mock-1', name: 'Neha Sharma', total: 18, reviewing: 5, interviewing: 7, offers: 4, placed: 2 },
  { id: 'mock-2', name: 'Rahul Verma', total: 15, reviewing: 4, interviewing: 6, offers: 3, placed: 2 },
  { id: 'mock-3', name: 'Priya Kapoor', total: 12, reviewing: 3, interviewing: 5, offers: 2, placed: 2 },
  { id: 'mock-4', name: 'Arjun Singh', total: 10, reviewing: 3, interviewing: 4, offers: 2, placed: 1 },
  { id: 'mock-5', name: 'Deepika Nair', total: 8, reviewing: 2, interviewing: 3, offers: 2, placed: 1 },
];

export function DashboardTeamActivity({ orgId }: { orgId: string | null }) {
  const q = useQuery<Application[]>({
    queryKey: ['dashboardAnalytics', 'applications-all', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      if (!orgId) return [];
      return applicationService.list(orgId, { limit: 500 });
    },
    staleTime: 60_000,
  });

  const rows = useMemo<Row[]>(() => {
    const apps = q.data ?? [];
    const byMember = new Map<string, Row>();
    for (const a of apps) {
      const memberId = a.assigned_to ?? UNASSIGNED_ID;
      const memberName = a.assigned_user?.display_name || a.assigned_user?.email || (memberId === UNASSIGNED_ID ? 'Unassigned' : 'Team member');
      const cur =
        byMember.get(memberId) ?? {
          memberId,
          memberName,
          total: 0,
          reviewing: 0,
          interviewing: 0,
          offers: 0,
          placed: 0,
        };
      cur.total += 1;
      if (REVIEWING_STATUSES.includes(a.status)) cur.reviewing += 1;
      if (INTERVIEW_STATUSES.includes(a.status)) cur.interviewing += 1;
      if (OFFER_STATUSES.includes(a.status)) cur.offers += 1;
      if (a.status === 'placed') cur.placed += 1;
      byMember.set(memberId, cur);
    }
    let result = Array.from(byMember.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
    
    if (DEMO_MOCK_ENABLED && result.length < 3) {
      result = [...result, ...MOCK_TEAM_MEMBERS.map(m => ({
        memberId: m.id,
        memberName: m.name,
        total: m.total,
        reviewing: m.reviewing,
        interviewing: m.interviewing,
        offers: m.offers,
        placed: m.placed,
      }))];
    }
    
    return result;
  }, [q.data]);

  const totals = useMemo(() => ({
    total: rows.reduce((s, r) => s + r.total, 0),
    reviewing: rows.reduce((s, r) => s + r.reviewing, 0),
    interviewing: rows.reduce((s, r) => s + r.interviewing, 0),
    offers: rows.reduce((s, r) => s + r.offers, 0),
    placed: rows.reduce((s, r) => s + r.placed, 0),
  }), [rows]);

  return (
    <section className="rounded-xl border vscode-border vscode-bg-secondary overflow-hidden">
      <header className="px-5 py-4 border-b vscode-border" style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(59, 130, 246, 0.05) 100%)' }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(139, 92, 246, 0.15)' }}>
              <Users className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <h2 className="text-sm font-semibold vscode-text-primary">Team activity</h2>
              <p className="text-xs vscode-text-tertiary">Applications per assignee</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-lg font-bold text-purple-500">{totals.total}</p>
              <p className="text-[10px] vscode-text-tertiary">Total apps</p>
            </div>
          </div>
        </div>
      </header>

      <div className="p-4">
        {q.isError ? (
          <div className="flex items-center justify-between gap-2 text-xs vscode-text-tertiary py-4">
            <span>Could not load team activity.</span>
            <button
              type="button"
              onClick={() => void q.refetch()}
              className="rounded-lg border vscode-border px-3 py-1.5 text-xs hover:vscode-bg-hover hover:vscode-text-primary"
            >
              Retry
            </button>
          </div>
        ) : q.isLoading ? (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg vscode-bg-tertiary" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(156, 163, 175, 0.15)' }}>
              <Users className="w-5 h-5 vscode-text-tertiary" />
            </div>
            <p className="text-sm vscode-text-tertiary">No team activity yet.</p>
          </div>
        ) : (
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[520px] table-auto border-collapse text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider vscode-text-tertiary border-b vscode-border">
                  <th className="py-3 pr-3 font-semibold">Member</th>
                  <th className="py-3 pr-3 text-right font-semibold">Total</th>
                  <th className="py-3 pr-3 text-right font-semibold">Reviewing</th>
                  <th className="py-3 pr-3 text-right font-semibold">Interviewing</th>
                  <th className="py-3 pr-3 text-right font-semibold">Offers</th>
                  <th className="py-3 text-right font-semibold">Placed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
                {rows.map((r, idx) => (
                  <tr key={r.memberId} className="hover:vscode-bg-hover transition-colors">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-semibold text-white"
                          style={{ background: `linear-gradient(135deg, ${['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444'][idx % 5]} 0%, ${['#7C3AED', '#1D4ED8', '#059669', '#D97706', '#DC2626'][idx % 5]} 100%)` }}
                        >
                          {r.memberName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <span className="vscode-text-primary font-medium truncate">{r.memberName}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <span className="font-semibold text-purple-500 tabular-nums">{r.total}</span>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs tabular-nums" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3B82F6' }}>
                        {r.reviewing}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs tabular-nums" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B' }}>
                        {r.interviewing}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs tabular-nums" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8B5CF6' }}>
                        {r.offers}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10B981' }}>
                        <CheckCircle2 className="w-3 h-3" />
                        {r.placed}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
